import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from '../i18n/translations';
import { isSpanishOnlyPath, languageFromPath, localizedPath } from '../lib/localeRoutes';

const SUPPORTED_LANGUAGES = ['es', 'en'];
const LanguageContext = createContext(null);

function getInitialLanguage() {
  return typeof window === 'undefined' ? 'es' : languageFromPath(window.location.pathname);
}

function readPath(source, path) {
  return path.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), source);
}

export function formatTranslation(template, params = {}) {
  if (typeof template !== 'string') return '';
  return template.replace(/\$\{(\w+)\}/g, (_, key) => params[key] ?? '');
}

export function translate(language, key, params) {
  const value = readPath(translations[language], key) ?? readPath(translations.es, key) ?? key;
  return formatTranslation(value, params);
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (nextLanguage) => {
    const safeLanguage = SUPPORTED_LANGUAGES.includes(nextLanguage) ? nextLanguage : 'es';
    if (isSpanishOnlyPath(window.location.pathname) && safeLanguage === 'en') return;
    const nextPath = localizedPath(`${window.location.pathname}${window.location.search}${window.location.hash}`, safeLanguage);
    if (nextPath !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.location.assign(nextPath);
    } else {
      setLanguageState(safeLanguage);
    }
  };

  const value = useMemo(() => ({
    language,
    languages: SUPPORTED_LANGUAGES,
    setLanguage,
    t: (key, params) => translate(language, key, params),
  }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
}
